import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { calendar, moduleColors } from '../data/studyData';
import { format, addDays, subDays } from 'date-fns';
import { Clock, BookOpen, Video, MessageSquare, Ship, CheckCircle2, ChevronLeft, ChevronRight, GraduationCap, Heart, Train } from 'lucide-react';

const DayCard = ({ date, completedTasks, onToggleTask, isFirst }) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const dayData = calendar.find(d => d.date === dateStr);
  const completedForDay = completedTasks[dateStr] || [];

  if (!dayData) {
    return (
      <Card className="rounded-2xl border-2 shadow-sm h-full">
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground font-medium">
            {format(date, 'EEEE')}
          </p>
          <CardTitle className="font-heading text-xl">
            {format(date, 'MMMM d')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
              <BookOpen className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">No study data</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const moduleStyle = moduleColors[dayData.module] || moduleColors.Rest;
  const isRestDay = dayData.module === 'Rest';
  const isRetreat = dayData.isRetreat;
  const isExam = dayData.isExam;
  const isWedding = dayData.isWedding;
  const isTravel = dayData.isTravel;

  return (
    <Card 
      className={`rounded-2xl border-2 shadow-sm ${isRetreat ? 'retreat-day' : ''} ${isFirst ? 'ring-2 ring-primary ring-offset-2' : ''} ${isExam ? 'border-red-500 dark:border-red-600' : ''} ${isWedding ? 'border-pink-500 dark:border-pink-600' : ''}`}
      data-testid={`day-card-${dateStr}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm text-muted-foreground font-medium">
              {format(date, 'EEEE')}
            </p>
            <CardTitle className="font-heading text-xl">
              {format(date, 'MMMM d')}
            </CardTitle>
          </div>
          <Badge 
            className={`${isExam ? 'bg-red-600 text-white' : isWedding ? 'bg-pink-600 text-white' : isTravel ? 'bg-orange-600 text-white' : moduleStyle.badgeBg + ' ' + moduleStyle.badgeText} rounded-full px-2 py-0.5 text-xs`}
          >
            {isExam ? 'EXAM' : isWedding ? 'WEDDING' : isTravel ? 'TRAVEL' : dayData.module}
          </Badge>
        </div>
        
        {isExam && (
          <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
            <GraduationCap className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="text-sm font-bold text-red-700 dark:text-red-300">
              EXAM DAY
            </span>
          </div>
        )}
        
        {isWedding && (
          <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-pink-100 dark:bg-pink-900/30">
            <Heart className="w-4 h-4 text-pink-600 dark:text-pink-400" />
            <span className="text-sm font-bold text-pink-700 dark:text-pink-300">
              Saj & Ailsa's Wedding
            </span>
          </div>
        )}
        
        {isTravel && (
          <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30">
            <Train className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <span className="text-sm font-bold text-orange-700 dark:text-orange-300">
              Travel Day
            </span>
          </div>
        )}
        
        {isRetreat && (
          <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30">
            <Ship className="w-3 h-3 text-violet-600 dark:text-violet-400" />
            <span className="text-xs font-medium text-violet-700 dark:text-violet-300">
              Study Retreat
            </span>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* Topic */}
        <div className={`p-3 rounded-xl ${moduleStyle.bg} ${moduleStyle.border} border`}>
          <h3 className={`font-heading font-semibold text-sm ${moduleStyle.text}`}>
            {dayData.topic}
          </h3>
        </div>

        {/* Study Hours */}
        {dayData.study_hours > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/50">
            <Clock className="w-4 h-4 text-sky-600" />
            <span className="text-sm font-medium">{dayData.study_hours} hours</span>
          </div>
        )}

        {/* Tasks */}
        {dayData.tasks.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                Tasks ({completedForDay.length}/{dayData.tasks.length})
              </h4>
            </div>
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-1.5 pr-2">
                {dayData.tasks.map((task, index) => {
                  const isCompleted = completedForDay.includes(index);
                  
                  return (
                    <div 
                      key={index}
                      className={`task-item flex items-start gap-2 p-2 rounded-lg border 
                        ${isCompleted ? 'bg-muted/30 border-muted' : 'bg-card border-border hover:border-primary/30'}
                        transition-colors cursor-pointer`}
                      onClick={() => onToggleTask(dateStr, index)}
                      data-testid={`task-${dateStr}-${index}`}
                    >
                      <Checkbox 
                        checked={isCompleted}
                        className="mt-0.5 h-4 w-4 rounded border-2"
                      />
                      <span className={`text-xs leading-relaxed ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                        {task}
                      </span>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Tutorials */}
        {dayData.tutorials.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Video className="w-3 h-3 text-amber-600" />
              <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                Tutorials
              </h4>
            </div>
            {dayData.tutorials.map((tutorial, index) => (
              <div 
                key={index}
                className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
              >
                <p className="text-xs font-medium text-amber-900 dark:text-amber-100">
                  {tutorial}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        {dayData.notes && (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-3 h-3 text-muted-foreground" />
              <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                Notes
              </h4>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed p-2 rounded-lg bg-muted/30">
              {dayData.notes}
            </p>
          </div>
        )}

        {/* Rest Day Message */}
        {isRestDay && (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <span className="text-2xl mb-2">😴</span>
            <p className="font-heading font-semibold text-sm">Rest Day</p>
            {dayData.notes && (
              <p className="text-xs text-muted-foreground mt-1">{dayData.notes}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DailyAgenda = ({ selectedDate, completedTasks, onToggleTask, onDateSelect, showNavigation = false }) => {
  const today = selectedDate;
  const tomorrow = addDays(today, 1);
  const dayAfter = addDays(today, 2);

  const handlePrevDays = () => {
    if (onDateSelect) onDateSelect(subDays(selectedDate, 3));
  };

  const handleNextDays = () => {
    if (onDateSelect) onDateSelect(addDays(selectedDate, 3));
  };

  const handleToday = () => {
    if (onDateSelect) onDateSelect(new Date());
  };

  return (
    <div className="space-y-4" data-testid="daily-agenda">
      {/* Navigation */}
      {showNavigation && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevDays}
            data-testid="prev-days-btn"
            className="rounded-xl border-2"
            aria-label="Previous 3 days"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={handleToday}
            data-testid="agenda-today-btn"
            className="rounded-xl border-2 px-6"
          >
            Today
          </Button>
          <Button
            variant="outline"
            onClick={handleNextDays}
            data-testid="next-days-btn"
            className="rounded-xl border-2"
            aria-label="Next 3 days"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Three Day Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DayCard 
          date={today} 
          completedTasks={completedTasks} 
          onToggleTask={onToggleTask}
          isFirst={true}
        />
        <DayCard 
          date={tomorrow} 
          completedTasks={completedTasks} 
          onToggleTask={onToggleTask}
          isFirst={false}
        />
        <DayCard 
          date={dayAfter} 
          completedTasks={completedTasks} 
          onToggleTask={onToggleTask}
          isFirst={false}
        />
      </div>
    </div>
  );
};
